import React, { useState, useEffect, useCallback } from 'react';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import config from '../config';

const LeagueInviteManager = ({ leagueId, currentUser, onClose, showToast, draftHasBegun = false }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteMessage, setNewInviteMessage] = useState('');

  const fetchInvites = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_URL}/leagues/${leagueId}/invites?user_id=${currentUser.id}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch invites');
      }
      
      const data = await response.json();
      setInvites(data);
    } catch (err) {
      setError('Failed to load invites');
      console.error('Error fetching invites:', err);
    } finally {
      setLoading(false);
    }
  }, [leagueId, currentUser.id]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (draftHasBegun) {
      showToast(
        'Invites are closed after the draft has started. Use Transfer team in League Settings to assign a replacement manager.',
        'error'
      );
      return;
    }
    if (!newInviteEmail.trim()) return;

    try {
      setSendingInvite(true);
      const response = await fetch(`${config.API_URL}/leagues/${leagueId}/invites?user_id=${currentUser.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: newInviteEmail.trim(),
          message: newInviteMessage.trim() || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send invite');
      }

      await response.json();
      showToast(`Invitation sent to ${newInviteEmail}!`, 'success');
      
      // Clear form
      setNewInviteEmail('');
      setNewInviteMessage('');
      
      // Refresh invites list
      await fetchInvites();
      
    } catch (err) {
      showToast(err.message, 'error');
      console.error('Error sending invite:', err);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const response = await fetch(`${config.API_URL}/leagues/${leagueId}/invites/${inviteId}?user_id=${currentUser.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to cancel invite');
      }

      showToast('Invitation cancelled', 'success');
      await fetchInvites();
    } catch (err) {
      showToast('Failed to cancel invite', 'error');
      console.error('Error cancelling invite:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'accepted': return '#059669';
      case 'expired': return '#dc2626';
      case 'cancelled': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'accepted': return 'Accepted';
      case 'expired': return 'Expired';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  if (loading) {
    return <LoadingSpinner text="Loading invites..." />;
  }

  if (error) {
    return <ErrorMessage error={error} onRetry={fetchInvites} />;
  }

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: 12,
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ margin: 0, color: '#2563eb' }}>League Invitations</h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 24,
                cursor: 'pointer',
                color: '#6b7280'
              }}
            >
              ×
            </button>
          </div>

          {draftHasBegun && (
            <div
              style={{
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: 8,
                padding: 14,
                marginBottom: 16,
                color: '#92400e',
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              The draft has started, so new email invitations are disabled. To add a replacement manager
              who inherits an existing roster, use <strong>Transfer team</strong> in Commissioner Tools.
            </div>
          )}

          {/* Send New Invite Form */}
          <div style={{ 
            background: '#f8fafc', 
            padding: 20, 
            borderRadius: 8, 
            marginBottom: 24,
            border: '1px solid #e2e8f0',
            opacity: draftHasBegun ? 0.6 : 1,
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#374151' }}>Send New Invitation</h3>
            <form onSubmit={handleSendInvite}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={newInviteEmail}
                  onChange={(e) => setNewInviteEmail(e.target.value)}
                  placeholder="friend@example.com"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 16
                  }}
                />
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151' }}>
                  Personal Message (Optional)
                </label>
                <textarea
                  value={newInviteMessage}
                  onChange={(e) => setNewInviteMessage(e.target.value)}
                  placeholder="Hey! I'd love for you to join my fantasy football league..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 16,
                    resize: 'vertical'
                  }}
                />
              </div>
              
              <button
                type="submit"
                disabled={draftHasBegun || sendingInvite || !newInviteEmail.trim()}
                style={{
                  background: sendingInvite ? '#9ca3af' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: 6,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: sendingInvite ? 'not-allowed' : 'pointer'
                }}
              >
                {sendingInvite ? 'Sending...' : 'Send Invitation'}
              </button>
            </form>
          </div>

          {/* Invites List */}
          <div>
            <h3 style={{ margin: '0 0 16px 0', color: '#374151' }}>Invitation History</h3>
            
            {invites.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 40, 
                color: '#6b7280',
                background: '#f9fafb',
                borderRadius: 8
              }}>
                No invitations sent yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {invites.map((invite) => (
                  <div
                    key={invite.id}
                    style={{
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: 16
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                          {invite.invitee_email}
                        </div>
                        <div style={{ fontSize: 14, color: '#6b7280' }}>
                          Sent by {invite.inviter_username} on {formatDate(invite.created_at)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            background: getStatusColor(invite.status),
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600
                          }}
                        >
                          {getStatusText(invite.status)}
                        </span>
                        {invite.status === 'pending' && (
                          <button
                            onClick={() => handleCancelInvite(invite.id)}
                            style={{
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: 4,
                              fontSize: 12,
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div style={{ fontSize: 14, color: '#6b7280' }}>
                      <div>Expires: {formatDate(invite.expires_at)}</div>
                      {invite.accepted_at && (
                        <div>Accepted: {formatDate(invite.accepted_at)}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeagueInviteManager; 